import Dialog from '@mui/material/Dialog';
import React, { useMemo } from 'react';
import { GoalProgressAddEntryForm } from 'shared/components/ui/goal-progress-add-entry-form';
import { DateFormatOptions } from 'shared/types/date-format-options';
import { MetricEntry, MetricUnit } from 'shared/types/goal-metric';
import { GoalProgressHistoryFormFields } from 'shared/types/goal-progress-form';
import { WeekDays } from 'shared/types/week-days';

import { GoalProgressUpdateError } from './goal-progress-update-error';
import { GoalProgressUpdateLoading } from './goal-progress-update-loading';

export type GoalProgressDialogProps = {
  open?: boolean;
  initialValues?: GoalProgressHistoryFormFields;
  unit?: MetricUnit;
  startValue?: number;
  dateFormat: DateFormatOptions;
  weekStartsOn: WeekDays;
  onClose: () => void;
  onSubmit: (entries: GoalProgressHistoryFormFields['entries']) => void;
  onOverview?: () => void;
  onRetry: () => void;
  isLoading?: boolean;
  isError?: boolean;
};

const defaultInitialValues: { entries: MetricEntry[] } = {
  entries: [],
};

export const GoalProgressUpdateDialog: React.FunctionComponent<
  GoalProgressDialogProps
> = ({
  initialValues = defaultInitialValues,
  unit,
  startValue,
  dateFormat,
  weekStartsOn,
  open,
  onClose,
  onSubmit,
  onOverview,
  onRetry,
  isLoading,
  isError,
}) => {
  const content = useMemo(() => {
    if (isError) {
      return <GoalProgressUpdateError onRetry={onRetry} onCancel={onClose} />;
    }

    if (isLoading) {
      return <GoalProgressUpdateLoading />;
    }

    return (
      <GoalProgressAddEntryForm
        entries={initialValues.entries}
        unit={unit}
        startValue={startValue}
        dateFormat={dateFormat}
        weekStartsOn={weekStartsOn}
        onSubmit={onSubmit}
        onOverview={onOverview}
        onClose={onClose}
      />
    );
  }, [
    isError,
    isLoading,
    initialValues.entries,
    unit,
    startValue,
    dateFormat,
    weekStartsOn,
    onSubmit,
    onOverview,
    onClose,
    onRetry,
  ]);

  return (
    <Dialog open={!!open} fullWidth>
      {content}
    </Dialog>
  );
};
